// ./src/components/features/Reminders/ReminderEditDrawer/ReminderEditDrawer.tsx
//
// Create/edit drawer for a scheduled event Reminder. Self-loading in edit mode via recordId; no success
// toast (the page highlight is the confirmation). Fields: name, kind (event-approaching / rsvp-nudge),
// channel (email / sms), schedule (before-event offset OR an absolute time), optional recurrence (every N
// days), audience (RSVP-status filter, event-approaching only), subject (email), message body, and an
// active/paused switch. In edit mode a "Preview recipients" button dry-runs the send to show who it would
// reach. `eventId` is set on CREATE only. Mirrors NoteEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import {
  Stack,
  TextInput,
  Textarea,
  SegmentedControl,
  NumberInput,
  Switch,
  Group,
  Box,
  Text,
  Alert,
  Button,
  Input,
  ActionIcon,
  Tooltip,
  Skeleton,
  Loader,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import {
  Reminder,
  ReminderInput,
  ReminderSendReport,
  ReminderService,
  ReminderCatalog,
  ReminderCatalogMessage,
  REMINDER_KIND,
  REMINDER_CHANNEL,
  REMINDER_SCHEDULE_KIND,
  EVENT_RSVP_STATUS,
  EVENT_RSVP_STATUS_LABELS,
} from "@/utils/api";
import { getFormErrors, formatErrors, utcToZonedString, zonedStringToUtc } from "@/utils";
import { SMS_PERSONAL_LINK_NOTICE } from "@/utils/sms";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalRemindersContext } from "@/contexts/RemindersContext";
import { AppDrawer, AppIcon, StatusInput, DateTimeField, MultiSelectField, SelectField } from "@/components/generic";
import { InsertVariableMenu } from "@/components/generic/VariableMenu/VariableMenu";
import { eventZoneNote } from "@/components/features/Events/eventZone";

import { OffsetUnit, offsetToParts, partsToOffset } from "../reminderDisplay";
import { REMINDER_VARIABLES, DEFAULT_NUDGE_RSVP_STATUSES } from "../reminderTemplates";

// The rich-text editor is heavy (Tiptap) — lazy-load it so it stays out of the shared bundle.
const RichTextField = React.lazy(() =>
  import("@/components/generic/RichTextField/RichTextField").then((m) => ({ default: m.RichTextField }))
);

// ── Catalog message helpers ──────────────────────────────────────────────────
// The catalog stores bodies as plain text; the email editor is HTML, so convert (mirrors the composer).
const escapeHtml = (s: string): string => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const plainToHtml = (text: string): string =>
  text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`)
    .join("");

const htmlHasText = (html: string): boolean =>
  html.replace(/<[^>]+>/g, "").replace(/&nbsp;/gi, " ").trim().length > 0;

// Whether the message body has host-entered content (channel-aware: HTML for email, plain for SMS).
const hasBodyText = (body: string, channel: string): boolean =>
  channel === REMINDER_CHANNEL.EMAIL ? htmlHasText(body) : body.trim().length > 0;

// Friendly labels for the tone facet, shown in the "Choose a message" picker.
const TONE_LABEL: Record<string, string> = {
  warm: "Friendly",
  playful: "Funny",
  formal: "Formal",
  simple: "Short & simple",
};

const catalogOptionLabel = (m: ReminderCatalogMessage): string => {
  const tone = (m.tone && TONE_LABEL[m.tone]) || m.label || "Message";
  return m.eventType ? `${tone} · tailored` : tone;
};

const KNOWN_FORM_FIELDS = [
  "name",
  "kind",
  "channel",
  "scheduleKind",
  "offsetMinutes",
  "repeatEveryDays",
  "sendAt",
  "messageSubject",
  "messageBody",
  "audience",
  "status",
];

export interface ReminderEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this reminder belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  /** In ADD mode, seed the form from this starter template (a "ghost" the host tapped). */
  presetReminder?: Reminder | null;
  onCreated?: (reminder: Reminder) => void;
  onUpdated?: (reminder: Reminder) => void;
  /**
   * The event's time zone — an absolute send time is picked, saved and shown on the EVENT's clock (ROADMAP #93).
   * ⚠ Required, and the caller mounts this drawer only once the event is loaded: a form seeded before the zone arrived
   * would be read in this device's zone and saved in the event's.
   */
  eventTimezone: string | null;
}

interface FormData {
  name: string;
  kind: string;
  channel: string;
  scheduleKind: string;
  offsetValue: number;
  offsetUnit: OffsetUnit;
  sendAtLocal: string | null;
  repeatEnabled: boolean;
  repeatEveryDays: number;
  rsvpStatuses: string[];
  messageSubject: string;
  messageBody: string;
  status: number;
}

const getInitialFormData = (): FormData => ({
  name: "",
  kind: REMINDER_KIND.EVENT_APPROACHING,
  channel: REMINDER_CHANNEL.EMAIL,
  scheduleKind: REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
  offsetValue: 1,
  offsetUnit: "days",
  sendAtLocal: null,
  repeatEnabled: false,
  repeatEveryDays: 3,
  rsvpStatuses: [],
  messageSubject: "",
  messageBody: "",
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: Reminder, tz: string | null): FormData => {
  const { value, unit } = offsetToParts(record.offsetMinutes);

  return {
    name: record.name || "",
    kind: record.kind || REMINDER_KIND.EVENT_APPROACHING,
    channel: record.channel || REMINDER_CHANNEL.EMAIL,
    scheduleKind: record.scheduleKind || REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
    offsetValue: value,
    offsetUnit: unit,
    sendAtLocal: utcToZonedString(record.sendAt, tz, false),
    repeatEnabled: !!record.repeatEveryDays,
    repeatEveryDays: record.repeatEveryDays || 3,
    rsvpStatuses: (record.audience?.rsvpStatus || []).filter((s): s is string => !!s),
    messageSubject: record.messageSubject || "",
    messageBody: record.messageBody || "",
    status: record.status ?? STATUSES.ACTIVE,
  };
};

const serialize = (data: FormData): string => JSON.stringify(data);

const ReminderEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={48} radius="sm" />
    <Skeleton height={36} radius="sm" />
    <Skeleton height={36} radius="sm" />
    <Skeleton height={80} radius="sm" />
    <Skeleton height={160} radius="sm" />
  </Stack>
);

export const ReminderEditDrawer: React.FC<ReminderEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  presetReminder,
  onCreated,
  onUpdated,
  eventTimezone,
}) => {
  const { t } = useTranslation(["events", "common"]);
  // The send time is the event's clock — said when this device keeps another zone.
  const zoneNote = eventZoneNote(t, eventTimezone, "elsewhere");

  const ctx = useOptionalRemindersContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const sendNowFn = ctx?.sendNow;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<Reminder | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as Reminder);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  // ── Dry-run recipient preview (edit mode) ──
  const [preview, setPreview] = useState<ReminderSendReport | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record, eventTimezone) : presetReminder ? fromRecord(presetReminder, eventTimezone) : getInitialFormData();
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    // A tapped template pre-fills real values, so treat it as dirty (there's something to save).
    setIsDirty(!isEditMode && !!presetReminder);
    setPreview(null);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, presetReminder, eventTimezone]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Append a {{token}} to a plain-text field (subject / SMS body). The rich-text email editor inserts
  // variables at the cursor via its own menu.
  const appendVariable = useCallback((field: "messageSubject" | "messageBody", key: string) => {
    setFormData((prev) => ({ ...prev, [field]: `${prev[field] || ""}{{${key}}}` }));
  }, []);

  const insertVariableLabel = t("events:reminders.form.insertVariable", { defaultValue: "Insert variable" });

  const subjectVariableMenu = (
    <InsertVariableMenu items={REMINDER_VARIABLES} onInsert={(key) => appendVariable("messageSubject", key)}>
      <Tooltip label={insertVariableLabel} withArrow>
        <ActionIcon variant="subtle" color="gray" aria-label={insertVariableLabel}>
          <AppIcon iconName="Braces" size={16} />
        </ActionIcon>
      </Tooltip>
    </InsertVariableMenu>
  );

  const subjectSmsVariableMenu = (
    <InsertVariableMenu items={REMINDER_VARIABLES} onInsert={(key) => appendVariable("messageBody", key)}>
      <Button variant="subtle" size="xs" color="gray" leftSection={<AppIcon iconName="Braces" size={14} />}>
        {insertVariableLabel}
      </Button>
    </InsertVariableMenu>
  );

  const isEmail = formData.channel === REMINDER_CHANNEL.EMAIL;
  const isBeforeEvent = formData.scheduleKind === REMINDER_SCHEDULE_KIND.BEFORE_EVENT;
  const isRsvpNudge = formData.kind === REMINDER_KIND.RSVP_NO_RESPONSE;

  // ── Message catalog (pick-a-message by kind + channel, like invites/updates) ──
  // On create, the event comes from the prop; on edit, from the loaded reminder.
  const effectiveEventId = eventId ?? record?.eventId ?? null;

  const [catalog, setCatalog] = useState<ReminderCatalogMessage[]>([]);
  const [catalogRecommendedId, setCatalogRecommendedId] = useState<string | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(null);

  // Live view of the current body (so the fetch effect can decide auto-apply without depending on it), and
  // the last message we auto-applied — so a kind/channel switch may replace an UNTOUCHED body but never a
  // host-edited one.
  const bodyRef = useRef(formData.messageBody);
  bodyRef.current = formData.messageBody;
  const lastAppliedBodyRef = useRef<string>("");

  // Load the picked message's subject + body into the form. Email bodies are HTML (convert from the plain
  // catalog text); SMS bodies are plain and have no subject.
  const applyCatalogMessage = useCallback((msg: ReminderCatalogMessage | null | undefined, channelNow: string) => {
    if (!msg) {
      return;
    }

    const emailNow = channelNow === REMINDER_CHANNEL.EMAIL;
    const body = emailNow ? plainToHtml(msg.body || "") : msg.body || "";

    setFormData((prev) => ({
      ...prev,
      messageBody: body,
      messageSubject: emailNow ? msg.subject || "" : prev.messageSubject,
    }));
    lastAppliedBodyRef.current = body;
    setSelectedCatalogId(msg.id ?? null);
  }, []);

  useEffect(() => {
    // Wait until the drawer is open, we know the event, and (in edit mode) the record has loaded.
    if (!opened || !effectiveEventId || (isEditMode && !record)) {
      return;
    }

    let isCancelled = false;
    setCatalogLoading(true);

    ReminderService.reminderCatalog(effectiveEventId, formData.kind, formData.channel).then((result) => {
      if (isCancelled) {
        return;
      }

      setCatalogLoading(false);

      if (!result.didSucceed()) {
        setCatalog([]);
        setCatalogRecommendedId(null);
        return;
      }

      const cat = result.getDataFirst() as ReminderCatalog | undefined;
      const messages = (cat?.messages || []).filter((m): m is ReminderCatalogMessage => !!m?.id);
      setCatalog(messages);
      setCatalogRecommendedId(cat?.recommendedId ?? null);

      // Auto-apply the recommended message when the body is still empty or is exactly what we last applied
      // (i.e. the host hasn't written their own). Never clobber a saved or hand-edited message.
      const current = bodyRef.current;
      const untouched = !hasBodyText(current, formData.channel) || current === lastAppliedBodyRef.current;

      if (untouched && messages.length > 0) {
        const recommended = messages.find((m) => m.id === cat?.recommendedId) || messages[0];
        applyCatalogMessage(recommended, formData.channel);
      } else {
        // Reflect a matching selection if the current body came from a known catalog entry, else clear it.
        setSelectedCatalogId((prev) => (messages.some((m) => m.id === prev) ? prev : null));
      }
    });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, effectiveEventId, formData.kind, formData.channel, isEditMode, record]);

  const rsvpStatusData = useMemo(
    () => Object.values(EVENT_RSVP_STATUS).map((value) => ({ value, label: EVENT_RSVP_STATUS_LABELS[value] || value })),
    []
  );

  // ── Build the input from the form ──
  const buildInput = useCallback((): ReminderInput => {
    const input: ReminderInput = {
      name: formData.name.trim() || null,
      kind: formData.kind,
      channel: formData.channel,
      scheduleKind: formData.scheduleKind,
      offsetMinutes: isBeforeEvent ? partsToOffset(formData.offsetValue, formData.offsetUnit) : null,
      sendAt: isBeforeEvent ? null : zonedStringToUtc(formData.sendAtLocal, eventTimezone),
      repeatEveryDays: formData.repeatEnabled ? Math.max(1, Math.round(formData.repeatEveryDays)) : null,
      audience: formData.rsvpStatuses.length > 0 ? { rsvpStatus: formData.rsvpStatuses } : null,
      messageSubject: isEmail ? formData.messageSubject.trim() || null : null,
      messageBody: formData.messageBody.trim() || null,
      status: formData.status,
    };

    if (!isEditMode) {
      input.eventId = eventId ?? null;
    }

    return input;
  }, [formData, isBeforeEvent, isEmail, isEditMode, eventId, eventTimezone]);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData = buildInput();

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as Reminder[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [buildInput, isEditMode, recordId, createFn, updateFn, onClose, onCreated, onUpdated]);

  const handlePreview = useCallback(async () => {
    if (!recordId || !sendNowFn) {
      return;
    }

    setPreviewLoading(true);
    setPreview(null);

    const result = await sendNowFn(recordId, true);

    if (result.didSucceed()) {
      setPreview((result.data as ReminderSendReport[])?.[0] ?? null);
    }

    setPreviewLoading(false);
  }, [recordId, sendNowFn]);

  // Switching to RSVP nudge with no audience picked yet → preselect the "no final answer" statuses, so
  // the host starts from a sensible default (and can see + change exactly who gets chased).
  const handleKindChange = useCallback((kind: string) => {
    setFormData((prev) => ({
      ...prev,
      kind,
      rsvpStatuses:
        kind === REMINDER_KIND.RSVP_NO_RESPONSE && prev.rsvpStatuses.length === 0
          ? [...DEFAULT_NUDGE_RSVP_STATUSES]
          : prev.rsvpStatuses,
    }));
  }, []);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("events:reminders.form.editTitle", { defaultValue: "Edit reminder" })
    : t("events:reminders.form.addTitle", { defaultValue: "Add reminder" });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <ReminderEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("events:reminders.form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("events:reminders.form.fields.name.placeholder", {
              defaultValue: "e.g. One week to go",
            })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <Input.Wrapper
            label={t("events:reminders.form.fields.kind.label", { defaultValue: "What is this reminder for?" })}
            error={fieldErrors.kind}
          >
            <SegmentedControl
              fullWidth
              value={formData.kind}
              onChange={handleKindChange}
              size="md"
              mt={4}
              data={[
                {
                  value: REMINDER_KIND.EVENT_APPROACHING,
                  label: t("events:reminders.kind.eventApproaching", { defaultValue: "Event approaching" }),
                },
                {
                  value: REMINDER_KIND.RSVP_NO_RESPONSE,
                  label: t("events:reminders.kind.rsvpNoResponse", { defaultValue: "RSVP nudge" }),
                },
              ]}
            />
          </Input.Wrapper>

          <MultiSelectField
            label={
              isRsvpNudge
                ? t("events:reminders.form.fields.nudgeAudience.label", { defaultValue: "Nudge guests who are" })
                : t("events:reminders.form.fields.audience.label", { defaultValue: "Send to (optional)" })
            }
            description={
              isRsvpNudge
                ? t("events:reminders.form.fields.nudgeAudience.description", {
                    defaultValue:
                      "Chase guests with these RSVP statuses — those who haven't given a final yes/no (invited, opened but didn't RSVP, or maybe).",
                  })
                : t("events:reminders.form.fields.audience.description", {
                    defaultValue:
                      "Limit to guests with these RSVP statuses. Leave empty to reach every attending guest. Reminders never go to hosts or crew.",
                  })
            }
            placeholder={
              isRsvpNudge
                ? t("events:reminders.form.fields.nudgeAudience.placeholder", { defaultValue: "Everyone with a pending RSVP" })
                : t("events:reminders.form.fields.audience.placeholder", { defaultValue: "Everyone" })
            }
            data={rsvpStatusData}
            value={formData.rsvpStatuses}
            onChange={(_name, rsvpStatuses) => setField("rsvpStatuses", rsvpStatuses)}
            error={fieldErrors.audience}
            clearable
            size="md"
          />

          <Input.Wrapper
            label={t("events:reminders.form.fields.channel.label", { defaultValue: "Send via" })}
            error={fieldErrors.channel}
          >
            <SegmentedControl
              fullWidth
              value={formData.channel}
              onChange={(value) => setField("channel", value)}
              size="md"
              mt={4}
              data={[
                { value: REMINDER_CHANNEL.EMAIL, label: t("events:reminders.channel.email", { defaultValue: "Email" }) },
                { value: REMINDER_CHANNEL.SMS, label: t("events:reminders.channel.sms", { defaultValue: "SMS" }) },
              ]}
            />
          </Input.Wrapper>

          <Input.Wrapper
            label={t("events:reminders.form.fields.schedule.label", { defaultValue: "When should it send?" })}
            error={fieldErrors.scheduleKind || fieldErrors.offsetMinutes || fieldErrors.sendAt}
          >
            <SegmentedControl
              fullWidth
              value={formData.scheduleKind}
              onChange={(value) => setField("scheduleKind", value)}
              size="md"
              mt={4}
              data={[
                {
                  value: REMINDER_SCHEDULE_KIND.BEFORE_EVENT,
                  label: t("events:reminders.schedule.beforeEvent", { defaultValue: "Before the event" }),
                },
                {
                  value: REMINDER_SCHEDULE_KIND.ABSOLUTE,
                  label: t("events:reminders.schedule.absolute", { defaultValue: "Specific time" }),
                },
              ]}
            />

            {isBeforeEvent ? (
              <Group gap="sm" mt="sm" align="flex-end" wrap="nowrap">
                <NumberInput
                  value={formData.offsetValue}
                  onChange={(v) => setField("offsetValue", typeof v === "number" ? v : Number(v) || 1)}
                  min={1}
                  max={9999}
                  size="md"
                  style={{ width: 120 }}
                  aria-label={t("events:reminders.form.fields.offsetValue.label", { defaultValue: "Amount" })}
                />
                <SelectField
                  aria-label={t("events:reminders.form.fields.offsetUnit.label", { defaultValue: "Unit" })}
                  value={formData.offsetUnit}
                  onChange={(_name, v) => v && setField("offsetUnit", v as OffsetUnit)}
                  data={[
                    { value: "minutes", label: t("events:reminders.offsetUnit.minutes", { defaultValue: "minutes" }) },
                    { value: "hours", label: t("events:reminders.offsetUnit.hours", { defaultValue: "hours" }) },
                    { value: "days", label: t("events:reminders.offsetUnit.days", { defaultValue: "days" }) },
                  ]}
                  allowDeselect={false}
                  size="md"
                  style={{ flex: 1 }}
                />
                <Text size="sm" c="dimmed" pb={8}>
                  {t("events:reminders.form.beforeStart", { defaultValue: "before the event starts" })}
                </Text>
              </Group>
            ) : (
              <Box mt="sm">
                <DateTimeField
                  name="sendAtLocal"
                  value={formData.sendAtLocal}
                  onChange={(_, v) => setField("sendAtLocal", v)}
                  placeholder={t("events:reminders.form.fields.sendAt.placeholder", { defaultValue: "Pick a date and time" })}
                  description={zoneNote ?? undefined}
                  size="md"
                  clearable
                />
              </Box>
            )}
          </Input.Wrapper>

          <Box>
            <Switch
              label={t("events:reminders.form.fields.repeat.label", { defaultValue: "Repeat until the event" })}
              description={
                <Text component="span" size="xs" c="dimmed">
                  {t("events:reminders.form.fields.repeat.description", {
                    defaultValue: "Re-send on a cadence until the event starts (great for RSVP nudges).",
                  })}
                </Text>
              }
              checked={formData.repeatEnabled}
              onChange={(e) => setField("repeatEnabled", e.currentTarget.checked)}
              size="md"
            />
            {formData.repeatEnabled && (
              <Group gap="sm" mt="sm" align="center" wrap="nowrap">
                <Text size="sm">{t("events:reminders.form.repeatEvery", { defaultValue: "Every" })}</Text>
                <NumberInput
                  value={formData.repeatEveryDays}
                  onChange={(v) => setField("repeatEveryDays", typeof v === "number" ? v : Number(v) || 1)}
                  min={1}
                  max={365}
                  size="md"
                  style={{ width: 100 }}
                  error={fieldErrors.repeatEveryDays}
                  aria-label={t("events:reminders.form.fields.repeatEveryDays.label", { defaultValue: "Days between sends" })}
                />
                <Text size="sm">{t("events:reminders.form.days", { defaultValue: "day(s)" })}</Text>
              </Group>
            )}
          </Box>

          {catalog.length > 0 && (
            <SelectField
              label={t("events:reminders.form.fields.catalog.label", { defaultValue: "Choose a message" })}
              description={t("events:reminders.form.fields.catalog.description", {
                defaultValue: "Auto-picked for your event — pick another tone to replace the message below.",
              })}
              placeholder={t("events:reminders.form.fields.catalog.placeholder", { defaultValue: "Choose a message" })}
              data={catalog.map((m) => ({
                value: m.id as string,
                label:
                  m.id === catalogRecommendedId
                    ? t("events:reminders.form.fields.catalog.recommended", {
                        defaultValue: "{{label}} · recommended",
                        label: catalogOptionLabel(m),
                      })
                    : catalogOptionLabel(m),
              }))}
              value={selectedCatalogId}
              onChange={(_name, id) => applyCatalogMessage(catalog.find((m) => m.id === id) || null, formData.channel)}
              rightSection={catalogLoading ? <Loader size="xs" /> : undefined}
              allowDeselect={false}
              size="md"
              comboboxProps={{ withinPortal: true }}
            />
          )}

          {isEmail && (
            <TextInput
              label={t("events:reminders.form.fields.subject.label", { defaultValue: "Email subject" })}
              placeholder={t("events:reminders.form.fields.subject.placeholder", {
                defaultValue: "Leave blank for a default subject",
              })}
              value={formData.messageSubject}
              onChange={(e) => setField("messageSubject", e.currentTarget.value)}
              error={fieldErrors.messageSubject}
              rightSection={subjectVariableMenu}
              rightSectionWidth={40}
              size="md"
            />
          )}

          <Input.Wrapper
            label={t("events:reminders.form.fields.message.label", { defaultValue: "Message" })}
            description={t("events:reminders.form.fields.message.description", {
              defaultValue: "The reminder your guests will receive. Insert {{variables}} to personalize it.",
            })}
            error={fieldErrors.messageBody}
          >
            <Box mt={4}>
              {isEmail ? (
                <Suspense fallback={<Skeleton height={200} radius="sm" />}>
                  <RichTextField
                    value={formData.messageBody}
                    onChange={(html) => setField("messageBody", html)}
                    placeholder={t("events:reminders.form.fields.message.placeholder", {
                      defaultValue: "e.g. Just a friendly reminder that our event is coming up — we hope to see you there!",
                    })}
                    variables={REMINDER_VARIABLES}
                    minHeight={200}
                    allowMedia={false}
                  />
                </Suspense>
              ) : (
                <Stack gap="xs">
                  <Group justify="flex-end">{subjectSmsVariableMenu}</Group>
                  <Textarea
                    placeholder={t("events:reminders.form.fields.message.smsPlaceholder", {
                      defaultValue: "e.g. Reminder: {{eventName}} is coming up — see you there!",
                    })}
                    value={formData.messageBody}
                    onChange={(e) => setField("messageBody", e.currentTarget.value)}
                    autosize
                    minRows={4}
                    maxRows={10}
                    size="md"
                  />
                  {/* #75 — what the server appends (ReminderCore.buildReminderMessage), so the host is not surprised. */}
                  <Text size="md" c="dimmed">
                    {isRsvpNudge
                      ? `The guest's own RSVP link goes at the end (unless your message already includes it), then: “${SMS_PERSONAL_LINK_NOTICE}”`
                      : "The event page link goes at the end (unless your message already includes it)."}
                  </Text>
                </Stack>
              )}
            </Box>
          </Input.Wrapper>

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            label={t("events:reminders.form.fields.status.label", { defaultValue: "Status" })}
            description={t("events:reminders.form.fields.status.description", {
              defaultValue: "A disabled reminder won't send automatically until you set it back to Active.",
            })}
            error={fieldErrors.status}
          />

          {isEditMode && (
            <Box>
              <Button
                variant="light"
                size="sm"
                leftSection={<AppIcon iconName="Users" size={16} />}
                onClick={handlePreview}
                loading={previewLoading}
              >
                {t("events:reminders.form.previewRecipients", { defaultValue: "Preview recipients" })}
              </Button>
              {preview && (
                <Alert mt="sm" color="blue" variant="light" icon={<AppIcon iconName="Users" size={16} />}>
                  {t("events:reminders.form.previewResult", {
                    defaultValue: "{{withContact}} of {{recipientsTotal}} matching guests can be reached on this channel.",
                    withContact: preview.withContact ?? 0,
                    recipientsTotal: preview.recipientsTotal ?? 0,
                  })}
                </Alert>
              )}
            </Box>
          )}
        </Stack>
      )}
    </AppDrawer>
  );
};
